import { useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
    getEAOpenCampaigns,
    getPlusExclusiveCampaign,
    getTabs,
} from '@client/redux/modules/mylounge';
import { getSemanticArticlesCarouselData } from '@client/redux/modules/semanticArticles';
import type { Campaign } from '@shared/types/mylounge';
import { usePlusExclusiveCampaignPosition } from '@client/components/mylounge/plus/use-plus-exclusive-campaign-position';
import { useContinueShopping } from '@client/components/continue-shopping/hooks/use-continue-shopping';
import { useNewDeals } from '@client/components/mylounge/newDeals';

import { useLeavingSoon } from '@client/components/mylounge/leavingSoon/hooks/useLeavingSoon';
import { CampaignWithLeavingSoonFlag } from '@client/components/mylounge/leavingSoon/types';
import { useGetCampaignsCarousel } from '@client/components/mylounge/campaignsCarousel/hooks/useGetCampaignsCarousel';
import type { GridLayoutContextValue } from './GridLayoutContext';

import { createCompleteCampaignList } from '../helpers';

import { useTopPicks4UGenderData, useIsEACarouselVariant } from '../hooks';

export interface UseGridLayoutContextValueProps {
    campaigns: Campaign[];
    isPlusEnabled: boolean;
    columnCount: number;
}

export type ActiveFeature = 'newDeals' | 'leavingSoon';
export const getActiveFeature = (
    isNewDealFeatureEnabled: boolean,
    isLeavingSoonComponent: boolean,
): ActiveFeature | null => {
    if (isNewDealFeatureEnabled) {
        return 'newDeals';
    }

    return isLeavingSoonComponent ? 'leavingSoon' : null;
};

export const getCampaignsForGridWithLeavingSoonLabel = (
    isLeavingSoonLabel: boolean,
    campaigns: Campaign[],
    leavingSoonCampaigns: Campaign[] | CampaignWithLeavingSoonFlag[],
) => {
    if (!isLeavingSoonLabel) {
        return campaigns;
    }

    // Create a map of leavingSoonCampaigns by campaignId for quick lookup
    const leavingSoonMap = new Map(leavingSoonCampaigns.map(c => [c.campaignId, c]));

    // Merge: take from leavingSoonMap if exists, otherwise from campaigns
    // Then add any leavingSoon campaigns not already in campaigns
    const mergedCampaigns = campaigns.map(c =>
        leavingSoonMap.has(c.campaignId) ? leavingSoonMap.get(c.campaignId)! : c,
    );

    // Add leavingSoonCampaigns that weren't in original campaigns
    const campaignIds = new Set(campaigns.map(c => c.campaignId));
    const newLeavingSoon = leavingSoonCampaigns.filter(c => !campaignIds.has(c.campaignId));

    return [...mergedCampaigns, ...newLeavingSoon];
};

// TODO: This hook is becoming quite large and may benefit from splitting into smaller hooks or using a state management solution if it continues to grow. For now, it's manageable but should be monitored as new features are added.
export const useGridLayoutContextValue = ({
    campaigns,
    isPlusEnabled,
    columnCount,
}: UseGridLayoutContextValueProps): GridLayoutContextValue => {
    // Fetch data sources
    const { isNewDealFeatureEnabled, campaignsForNewDeal } = useNewDeals();
    const {
        isLeavingSoonLabel,
        isLeavingSoonComponent,
        campaignsForLeavingSoonCarousel,
        leavingSoonCampaignsWithFlag,
    } = useLeavingSoon();

    // Determine active feature for layout logic
    const activeFeature = getActiveFeature(isNewDealFeatureEnabled, isLeavingSoonComponent);

    // Filter out New Deals Today campaigns from main campaign list if feature is enabled
    // use campaigns if feature is disabled
    const campaignsForGridWithTimedCarousel = useMemo(() => {
        if (activeFeature === null) return campaigns;

        const campaignsToExclude =
            activeFeature === 'newDeals' ? campaignsForNewDeal : campaignsForLeavingSoonCarousel;

        const excludeIds = new Set(campaignsToExclude.map(c => c.campaignId));
        return campaigns.filter(c => !excludeIds.has(c.campaignId));
    }, [campaigns, campaignsForNewDeal, activeFeature, campaignsForLeavingSoonCarousel]);

    const campaignsForGridWithLeavingSoonLabel = useMemo(
        () =>
            getCampaignsForGridWithLeavingSoonLabel(
                isLeavingSoonLabel,
                campaigns,
                leavingSoonCampaignsWithFlag,
            ),
        [isLeavingSoonLabel, campaigns, leavingSoonCampaignsWithFlag],
    );

    const campaignsForGrid = isLeavingSoonLabel
        ? campaignsForGridWithLeavingSoonLabel
        : campaignsForGridWithTimedCarousel;

    const carousels = useGetCampaignsCarousel(activeFeature);

    const { articles: semanticArticles } = useSelector(getSemanticArticlesCarouselData);
    const isSemanticArticleCarousel = (semanticArticles?.length ?? 0) > 0;
    const topPicks4UGenderData = useTopPicks4UGenderData();
    const { shouldShowContinueShopping } = useContinueShopping();
    // Merge campaigns with early access
    const earlyAccessOpenCampaignsRaw = useSelector(getEAOpenCampaigns);
    const exclusiveCampaign = useSelector(getPlusExclusiveCampaign);
    const tabsRaw = useSelector(getTabs);
    const isEACarousel = useIsEACarouselVariant();

    // to prevent to pass null/undefined values around.
    // In future we should refactor campaigns selector to be the UNIQUE source of truth
    // without filtering and additions
    const earlyAccessOpenCampaigns = useMemo(
        () => earlyAccessOpenCampaignsRaw ?? [],
        [earlyAccessOpenCampaignsRaw],
    );
    // to prevent to pass null/undefined values around
    const tabs = useMemo(() => tabsRaw ?? [], [tabsRaw]);

    // Memoize EA campaign logic to prevent cascading recalculations
    const earlyAccessCampaignLogic = useMemo(() => {
        const hasPlusOffersTab = tabs.some(tab => tab.event === 'PLUS_OFFER_EVENT');
        return {
            hasPlusOffersTab,
            numberOfEACampaignsShouldShown:
                isPlusEnabled && !hasPlusOffersTab ? earlyAccessOpenCampaigns.length : 0,
        };
    }, [tabs, isPlusEnabled, earlyAccessOpenCampaigns]);

    const plusExclusiveCampaignPosition = usePlusExclusiveCampaignPosition({
        columnCount,
        offset: earlyAccessCampaignLogic.numberOfEACampaignsShouldShown,
    });

    const completeCampaignsList = useMemo(
        () =>
            createCompleteCampaignList(
                campaignsForGrid,
                earlyAccessOpenCampaigns,
                exclusiveCampaign,
                plusExclusiveCampaignPosition,
                isEACarousel,
            ),
        [
            campaignsForGrid,
            earlyAccessOpenCampaigns,
            exclusiveCampaign,
            plusExclusiveCampaignPosition,
            isEACarousel,
        ],
    );

    const campaignIndexRef = useRef(0);

    // Reset index whenever campaign list or layout changes
    useEffect(() => {
        campaignIndexRef.current = 0;
    }, [completeCampaignsList, columnCount]);

    /**
     * Internal method to reset campaign index (called by DeclarativeGrid at render start).
     * Not exposed in context - DeclarativeGrid has direct access via hook.
     */
    const resetCampaignIndex = () => {
        campaignIndexRef.current = 0;
    };

    /**
     * Internal method to get and increment campaign index (called during child traversal).
     * Not exposed in context - only used internally for offset calculation.
     */
    const getAndIncrementIndex = (count: number = 1): number => {
        const currentIndex = campaignIndexRef.current;
        campaignIndexRef.current += count;
        return currentIndex;
    };

    const contextValue = useMemo(
        () => ({
            campaigns: completeCampaignsList,
            columnCount,
            carousels,
            topPicks4UGenderData,
            isPlusEnabled,
            isNewDealFeatureEnabled,
            isSemanticArticleCarousel,
            isContinueShopping: shouldShowContinueShopping,
            isEACarousel,
            isLeavingSoonLabel,
            isLeavingSoonComponent,
            campaignsForLeavingSoonCarousel,
        }),
        [
            completeCampaignsList,
            columnCount,
            carousels,
            topPicks4UGenderData,
            isPlusEnabled,
            isNewDealFeatureEnabled,
            isSemanticArticleCarousel,
            shouldShowContinueShopping,
            isEACarousel,
            isLeavingSoonLabel,
            isLeavingSoonComponent,
            campaignsForLeavingSoonCarousel,
        ],
    );

    /**
     * Create extended context value with internal methods for offset calculation.
     * Internal methods are prefixed with __ to indicate private implementation.
     */
    return {
        ...contextValue,
        _resetCampaignIndex: resetCampaignIndex,
        _getAndIncrementIndex: getAndIncrementIndex,
    };
};
